/**
 * Singleton that manages captcha driver registration and initialization
 */
class CaptchaManager {
  constructor() {
    this.drivers = new Map()
    this.activeDriver = null
  }

  /**
   * Register a driver class
   * @param {string} name - Driver name (e.g., 'recaptcha_v2', 'recaptcha_v3', 'turnstile')
   * @param {Class} driverClass - Driver class constructor
   */
  registerDriver(name, driverClass) {
    this.drivers.set(name, driverClass)
  }

  /**
   * Initialize from window.APP.captcha_driver
   * This should be called after all drivers are registered
   * @returns {Promise<void>}
   */
  async initialize() {
    const config = window.APP?.captcha_driver
    if (!config) {
      return Promise.resolve()
    }

    const DriverClass = this.drivers.get(config.name)
    if (!DriverClass) {
      console.warn(`Captcha driver "${config.name}" not found`)
      return Promise.resolve()
    }

    this.activeDriver = new DriverClass(config)
    return this.activeDriver.init()
  }

  /**
   * Get the configured captcha driver config before initialization completes.
   * @returns {{ name: string, public_key: string, can_render: boolean }|null}
   */
  getConfiguredDriverConfig() {
    return window.APP?.captcha_driver || null
  }

  /**
   * Check if a supported captcha driver is configured or initialized.
   * @returns {boolean}
   */
  hasDriver() {
    if (this.activeDriver) {
      return true
    }

    const config = this.getConfiguredDriverConfig()
    if (!config) {
      return false
    }

    return this.drivers.has(config.name)
  }

  /**
   * Check if the active captcha driver renders a visible widget.
   * @returns {boolean}
   */
  canRender() {
    if (this.activeDriver) {
      return this.activeDriver.canRender === true
    }

    const config = this.getConfiguredDriverConfig()
    if (!config || !this.drivers.has(config.name)) {
      return false
    }

    return config.can_render === true
  }

  /**
   * Get an on-demand token
   * @param {string} action
   * @param {{ container?: HTMLElement | null }} options
   * @returns {Promise<string|null>}
   */
  async getToken(action = 'submit', options = {}) {
    if (!this.activeDriver) {
      return null
    }
    return this.activeDriver.getToken(action, options)
  }

  /**
   * @param {HTMLElement} container
   */
  watchForElements(container = document.body) {
    if (!this.activeDriver?.canRender) {
      return
    }
    this.activeDriver.watchForElements(container)
  }

  /**
   * Setup automatic captcha for forms with data-captcha attribute
   * @param {HTMLElement} container - Container to watch
   */
  setupForms(container = document.body) {
    if (!this.activeDriver) {
      return
    }

    // Initialize existing forms
    const existingForms = container.querySelectorAll('form[data-captcha]')
    existingForms.forEach((form) => this.setupForm(form))

    // Watch for new forms
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches?.('form[data-captcha]')) {
              this.setupForm(node)
            }
            const forms = node.querySelectorAll?.('form[data-captcha]')
            forms?.forEach((form) => this.setupForm(form))
          }
        })
      })
    })

    observer.observe(container, {
      childList: true,
      subtree: true,
    })
  }

  /**
   * Setup captcha for a single form
   * @param {HTMLFormElement} form
   */
  setupForm(form) {
    // Prevent double-setup
    if (form.hasAttribute('data-captcha-initialized')) {
      return
    }
    form.setAttribute('data-captcha-initialized', 'true')

    if (this.activeDriver.canRender) {
      this.setupRenderableForm(form)
    } else {
      this.setupInvisibleForm(form)
    }
  }

  /**
   * Setup form for renderable captcha (V2/Turnstile)
   * @param {HTMLFormElement} form
   */
  setupRenderableForm(form) {
    // Check if .g-recaptcha already exists (manual placement or hook)
    if (form.querySelector('.g-recaptcha')) {
      return // Already has captcha element
    }

    // Look for explicit hook element
    const hook = form.querySelector('[data-captcha-hook]')
    if (hook) {
      // Inject into hook
      const captchaDiv = document.createElement('div')
      captchaDiv.className = 'g-recaptcha'
      hook.appendChild(captchaDiv)
      return
    }

    // Inject before submit button
    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]')
    if (!submitButton) {
      console.warn('Form has data-captcha but no submit button found', form)
      return
    }

    const captchaDiv = document.createElement('div')
    captchaDiv.className = 'g-recaptcha'
    captchaDiv.style.marginBottom = '1rem' // Add spacing

    submitButton.parentNode.insertBefore(captchaDiv, submitButton)

    // The existing watchForElements() will pick this up and render it
  }

  /**
   * Setup form for invisible captcha (V3)
   * @param {HTMLFormElement} form
   */
  setupInvisibleForm(form) {
    // Track if we're currently processing captcha
    let processingCaptcha = false

    form.addEventListener(
      'submit',
      async (ev) => {
        // If we already processed captcha, let event through
        if (processingCaptcha) {
          return
        }

        ev.preventDefault()
        ev.stopImmediatePropagation()

        // Get action from data-captcha attribute
        const action = form.getAttribute('data-captcha') || 'submit'

        // Get token
        const token = await this.getToken(action)

        if (token) {
          // Remove any existing g-recaptcha-response inputs
          form.querySelectorAll('input[name="g-recaptcha-response"]').forEach((input) => {
            input.remove()
          })

          // Add token as hidden input
          const hiddenInput = document.createElement('input')
          hiddenInput.setAttribute('type', 'hidden')
          hiddenInput.setAttribute('name', 'g-recaptcha-response')
          hiddenInput.setAttribute('value', token)
          form.appendChild(hiddenInput)
        }

        // Re-dispatch submit event (will be caught by other handlers)
        processingCaptcha = true
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true })
        const defaultNotPrevented = form.dispatchEvent(submitEvent)
        processingCaptcha = false

        // If no handler prevented default, this is a regular form - submit it
        if (defaultNotPrevented) {
          form.submit()
        }
      },
      { capture: true },
    )
  }
}

export default new CaptchaManager()
